import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, LineChart, Preloader, RawButton, Text, useResponsive } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import theme from '@/styles/theme';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';

import { useGetPerformanceHistoryQuery } from './queries';

const DEFAULT_CHART_WIDTH = 660;
const SHIFT_KOEF = 1.15;
const MIN_ELEMENTS = 3;

const PerformanceChart = () => {
  const { isMobile } = useResponsive();
  const parentRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChildWidth] = useState(DEFAULT_CHART_WIDTH);

  const { t } = useTranslation();
  const toggleOptions = useMemo(() => {
    return [
      {
        key: dayjs().diff(dayjs().startOf('year'), 'days'),
        title: t('YTD'),
      },
      {
        key: 180,
        title: t('180D'),
      },
      {
        key: 30,
        title: t('30D'),
      },

      {
        key: 7,
        title: t('7D'),
      },
    ];
  }, [t]);

  const [currentOption, setCurrentOption] = useState(toggleOptions[3].key);
  const { isLoading, values, hints, labels, valueHints } = useGetPerformanceHistoryQuery({
    daysBack: currentOption,
  });
  useEffect(() => {
    const handleResize = () => {
      if (parentRef.current) {
        setChildWidth(parentRef.current.offsetWidth);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isLoading]);
  const countDigits = useMemo(() => {
    const maxValue =
      _.max([Math.abs(_.min(values) || 0) * SHIFT_KOEF, Math.abs(_.max(values) || 0) * SHIFT_KOEF]) || 0;
    const roundedValue = _.round(maxValue, 0);

    return _.size(_.toString(roundedValue)) + 1;
  }, [values]);
  const currentLeftPadding = useMemo(() => {
    // It's not linear function and cant be calculated.
    if (countDigits > 8) {
      return 60;
    }
    if (countDigits > 6) {
      return 57;
    }
    return 52;
  }, [countDigits]);

  return (
    <Box
      data-testid="performance-chart"
      background="canvas"
      round="container1Round"
      height={{ min: '320px' }}
      pad="l"
    >
      <Box
        direction={isMobile ? 'column' : 'row'}
        key={currentLeftPadding}
        justify={isMobile ? 'start' : 'between'}
        align={isMobile ? 'start' : 'center'}
        ref={parentRef}
      >
        <Text weight={700}>{t('Investment Balance Over Time')}</Text>

        <Box direction="row" margin={isMobile ? { vertical: 'spacing16' } : {}}>
          {toggleOptions.map((item) => (
            <RawButton
              data-testid={`performance-chart_option_${item.key}`}
              key={item.key}
              onClick={() => setCurrentOption(item.key)}
              margin={isMobile ? { right: 'spacing8' } : { left: 'spacing8' }}
            >
              <Box
                pad={{ vertical: '10px', horizontal: 'm' }}
                round="button1Round"
                background={item.key === currentOption ? 'accentContainer' : ''}
                border={{ size: 'small', color: item.key === currentOption ? 'accentContainer' : 'border1' }}
              >
                <Text weight={400}>{item.title}</Text>
              </Box>
            </RawButton>
          ))}
        </Box>
      </Box>
      {isLoading ? (
        <Box fill align="center" justify="center" style={{ flex: 1 }}>
          <Preloader size={72} />
        </Box>
      ) : !values.length || values.length < MIN_ELEMENTS ? (
        <Box fill align="center" justify="center" style={{ flex: 1 }}>
          <Text>{t('We need a little more information to generate your performance chart. ')}</Text>
          <Text>{t(" Once you've been investing for a bit longer, your chart will appear here.")}</Text>
        </Box>
      ) : (
        <LineChart
          fontFamily={whiteLabelingConfig.font}
          width={chartWidth}
          key={`${currentOption} ${chartWidth} ${isLoading} ${values.length}`}
          values={values}
          tips={hints}
          paddingLeft={currentLeftPadding}
          paddingRight={22}
          labels={labels}
          lineColor={theme.colors.chart}
          showHorizontalLines={!isMobile}
          showVerticalLines={isMobile}
          showMinMax={isMobile}
          isLabelLess={isMobile}
          valueTips={valueHints}
        />
      )}
    </Box>
  );
};
export default PerformanceChart;
